import { sub } from "date-fns";
import type { DexieStorageInternals } from "rxdb";
import type { RxReplicationState } from "rxdb/dist/types/plugins/replication";

type DexieTable = Awaited<DexieStorageInternals>["dexieTable"];

const findExpiredReplicationDocuments = async ({
  table,
  expiryLimit,
}: {
  table: DexieTable;
  expiryLimit: Date;
}): Promise<{ id: string; itemId: string }[]> =>
  (
    (await table.toArray()) as {
      id: string;
      itemId: string;
      _meta: { lwt: number };
    }[]
  ).filter(({ _meta }) => _meta.lwt < expiryLimit.getTime());

/**
 * Deletes data last written before the expiry limit from the database, along
 * with any associated replication state.
 *
 * This method should only be used for collections that you don't plan to pull
 * endlessly - i.e. at time of writing, how we currently pull patients,
 * investigations, and presriptions. That's because it will clear the checkpoint
 * from the replication state and would reset any progress made in pulling the
 * data. This is acceptable in this situation given we're using a trick to only
 * pull select pieces of this information, as opposed to whole collecitons.
 *
 * It is important we clear the checkpoint data, becuase it contains a replica
 * of the PII/PHI data we're trying to trim for security reasons.
 *
 * The bug I foresee is that we find a document that's valid to delete, in that
 * moment the user also loads that patient. We then delete it, and the user
 * updates it. This then falls apart as the document can no longer be found.
 * It should be exceedingly rare.
 */
export const clearExpiredData = async <T, C>({
  replicationState,
  maxAge,
  clearPersistedCheckpoints,
}: {
  replicationState: RxReplicationState<T, C>;
  maxAge: Duration;
  clearPersistedCheckpoints: () => Promise<void>;
}) => {
  await waitForReplicationToBeInactive(replicationState);

  const expiryLimit = sub(new Date(), maxAge);

  const coreTableInternals = await (replicationState.collection.storageInstance
    .internals as DexieStorageInternals);
  const replTableInternals = await (replicationState.metaInstance
    .internals as DexieStorageInternals);

  const expiredReplicationDocs = await findExpiredReplicationDocuments({
    table: replTableInternals.dexieTable,
    expiryLimit,
  });

  const result = await Promise.allSettled([
    coreTableInternals.dexieTable.bulkDelete(
      expiredReplicationDocs
        .map(({ itemId }) => itemId)
        // These are the replication checkpoints that exist only in the
        // replication table, thus we shouldn't try to delete them here.
        .filter((itemId) => !["up", "down"].includes(itemId)),
    ),
    replTableInternals.dexieTable.bulkDelete(
      // For now, we will include the checkpoint documents for deletion, so that
      // should you visit a patient that was contained in your checkpoint, it
      // will be re-pulled.
      expiredReplicationDocs.map(({ id }) => id),
    ),
    clearPersistedCheckpoints(),
  ]);
  if (result.some(({ status }) => status === "rejected")) {
    console.error("Error during hard delete", { result });
  } else if (expiredReplicationDocs.length > 0) {
    console.debug("Deleted expired data.", {
      count: expiredReplicationDocs.length,
      trimmedUntil: expiryLimit.toISOString(),
    });
  }
};

/* TODO: Decide whether this hook is desired 
Maybe it will be useful to decouple the replication logic from
the data expiry logic - either use it or remove this commented
out code. 
*/

// export const useDataExpiryInterval = <T, C>({
//   replicationState,
// }: {
//   replicationState?: RxReplicationState<T, C>;
// }) => {
//   const { rxdb } = useConfig();
//   const [dataExpiryEnabled] = useFeatureFlag(featureFlags.rxdbDataExpiry);

//   return useInterval(
//     () => {
//       if (!dataExpiryEnabled) {
//         console.debug("Data expiry not enabled, skipping data expiry.");
//         return;
//       }
//       if (!replicationState) {
//         console.debug(
//           "Collection or replication state not provided, skipping data expiry.",
//         );
//         return;
//       }
//       return clearExpiredData({
//         replicationState,
//         maxAge: { minutes: rxdb?.dataExpiryMaxAgeMins ?? 60 * 24 * 2 },
//          clearPersistedCheckpoints: async () => {}
//       });
//     },
//     2 * 60 * 1000,
//   );
// };

/**
 * Wait for the `active.up` sync to no longer be active before continuing with deletion.
 * Based on my debugging, the replicationState.awaitInSync method only actually
 * waits for the initial sync to take place, rather than generally awaiting
 * what's currently being synchronised; it listens for the `firstSyncDone` event
 * (see here: https://github.com/pubkey/rxdb/blob/60d6c5760b6a874c7d448757dac960fcb717a2bf/src/replication-protocol/index.ts#L116)
 * which was not re-firing when I was unit testing this code, hence the listener
 * on `active` here instead.
 */
export const waitForReplicationToBeInactive = async <T, C>(
  replicationState?: RxReplicationState<T, C>,
) => {
  return await new Promise((resolve) =>
    replicationState?.internalReplicationState?.events.active.up.subscribe(
      (isActive) => {
        if (!isActive) {
          resolve(isActive);
        }
      },
    ),
  );
};
